import type { User } from '@/payload-types'

export const INTRANET_PREVIEW_COOKIE_NAME = 'intranet_preview'

export type IntranetRole = NonNullable<User['intranet_roles']>[number]
export type LoginRole = NonNullable<User['roles']>[number]

/**
 * Sentinel preview value that represents "no role" — i.e. the experience a
 * logged-in user with neither the `admin` global role nor any
 * `intranet_roles` would get.
 */
export const INTRANET_PREVIEW_NONE = 'none' as const
export type IntranetPreviewNone = typeof INTRANET_PREVIEW_NONE

/**
 * Synthetic preview value that represents a logged-in user who is a confirmed
 * volunteer for the event they are currently viewing. It is not a real
 * `intranet_roles` value; it simply unlocks the same event-specific data path
 * that real volunteer membership unlocks (e.g. the team-leader contact list)
 * without granting any intranet role privileges.
 */
export const INTRANET_PREVIEW_CONFIRMED_VOLUNTEER = 'confirmed_volunteer' as const
export type IntranetPreviewConfirmedVolunteer = typeof INTRANET_PREVIEW_CONFIRMED_VOLUNTEER

export type IntranetPreviewValue =
  | IntranetRole
  | IntranetPreviewNone
  | IntranetPreviewConfirmedVolunteer

export const INTRANET_PREVIEW_ROLE_VALUES: IntranetRole[] = [
  'team_leader',
  'regional_coordinator',
  'talent_rep',
  'sponsor',
]

export const INTRANET_PREVIEW_VALUES: IntranetPreviewValue[] = [
  ...INTRANET_PREVIEW_ROLE_VALUES,
  INTRANET_PREVIEW_CONFIRMED_VOLUNTEER,
  INTRANET_PREVIEW_NONE,
]

export const INTRANET_PREVIEW_LABELS: Record<IntranetPreviewValue, string> = {
  team_leader: 'Team Leader',
  regional_coordinator: 'Regional Coordinator',
  talent_rep: 'Talent Rep',
  sponsor: 'Sponsor',
  confirmed_volunteer: 'Confirmed Volunteer',
  none: 'No Role',
}

export function isConfirmedVolunteerPreview(
  value: IntranetPreviewValue | null | undefined,
): value is IntranetPreviewConfirmedVolunteer {
  return value === INTRANET_PREVIEW_CONFIRMED_VOLUNTEER
}

export function isIntranetPreviewValue(value: unknown): value is IntranetPreviewValue {
  return (
    typeof value === 'string' &&
    INTRANET_PREVIEW_VALUES.includes(value as IntranetPreviewValue)
  )
}

/**
 * Only top-level Intranet users can engage intranet role emulation. This is used as
 * both a server-side gate (so non-privileged users cannot self-escalate by
 * manually setting the preview cookie) and a client-side gate (so the banner
 * never shows for a user who happens to have the cookie set for any reason).
 */
export function canUserPreviewIntranetRoles(roles: LoginRole[] | null | undefined): boolean {
  const list = roles ?? []
  return list.includes('intranet')
}

/**
 * True when the user has team-leader-level access or higher (admin or
 * regional coordinator). Used to surface elevated event actions in lists and
 * dashboards. Matches the gating applied in `event-management-subnav` and
 * advanced event list items.
 */
export function isTeamLeaderOrAbove(
  roles: LoginRole[] | null | undefined,
  intranetRoles: IntranetRole[] | null | undefined,
): boolean {
  const roleList = roles ?? []
  if (roleList.includes('admin')) return true
  const intranet = intranetRoles ?? []
  return intranet.includes('team_leader') || intranet.includes('regional_coordinator')
}

/**
 * True when the user is allowed to change an event's status directly from the
 * public event page. Intentionally narrower than `isTeamLeaderOrAbove`: only
 * `admin`/`intranet` staff and regional coordinators qualify. Team leaders and
 * regular volunteers are explicitly excluded.
 */
export function canManageEventStatus(
  roles: LoginRole[] | null | undefined,
  intranetRoles: IntranetRole[] | null | undefined,
): boolean {
  const roleList = roles ?? []
  if (roleList.includes('admin') || roleList.includes('intranet')) return true
  return (intranetRoles ?? []).includes('regional_coordinator')
}

export type EventStatus = 'pending' | 'participating' | 'not-participating'

/**
 * Applies direction-aware permissions to event status changes:
 * - Confirmed -> NP requires RC or above.
 * - NP -> confirmed requires Artist Relations or field staff.
 * - Changes involving pending retain the existing event-status permissions.
 */
export function canTransitionEventStatus(
  from: EventStatus,
  to: EventStatus,
  roles: LoginRole[] | null | undefined,
  intranetRoles: IntranetRole[] | null | undefined,
): boolean {
  const roleList = roles ?? []
  const intranetRoleList = intranetRoles ?? []
  const hasStaffBypass =
    roleList.includes('admin') || roleList.includes('cms') || roleList.includes('intranet')

  if (from === 'participating' && to === 'not-participating') {
    return (
      hasStaffBypass ||
      intranetRoleList.includes('regional_coordinator') ||
      intranetRoleList.includes('talent_rep')
    )
  }

  if (from === 'not-participating' && to === 'participating') {
    return hasStaffBypass || intranetRoleList.includes('talent_rep')
  }

  return canManageEventStatus(roleList, intranetRoleList)
}

type EffectiveResolveArgs = {
  realRoles: LoginRole[]
  realIntranetRoles: IntranetRole[]
  previewValue: IntranetPreviewValue | null
}

/**
 * Global login roles that grant staff-level bypass access (e.g. event "More
 * Info" sections, Payload editing). These are suppressed while a preview is
 * active so the previewed experience is faithful — the previewing user always
 * holds the `intranet` login role (it's required to preview at all), so leaving
 * it in place would leak staff-only content into downgraded previews such as
 * "No Role".
 */
const STAFF_BYPASS_LOGIN_ROLES: LoginRole[] = ['admin', 'cms', 'intranet']

/**
 * Computes the effective `roles` and `intranet_roles` after applying a preview
 * value. When preview is active we strip the staff bypass global login roles
 * (`admin`, `cms`, `intranet`) from the effective list so their bypass logic in
 * role checks is suppressed — a staff user previewing "No Role" should
 * faithfully see the no-role experience. Team-leader/coordinator previews keep
 * their elevated access because it is granted through `intranet_roles`, not the
 * stripped login roles.
 *
 * Callers must independently confirm the preview value is allowed for the real
 * user (see `canUserPreviewIntranetRoles`) before calling this with a non-null
 * `previewValue`.
 */
export function resolveEffectiveRoles({
  realRoles,
  realIntranetRoles,
  previewValue,
}: EffectiveResolveArgs): { effectiveRoles: LoginRole[]; effectiveIntranetRoles: IntranetRole[] } {
  if (!previewValue) {
    return {
      effectiveRoles: realRoles,
      effectiveIntranetRoles: realIntranetRoles,
    }
  }

  const effectiveRoles = realRoles.filter((role) => !STAFF_BYPASS_LOGIN_ROLES.includes(role))

  // Sentinel preview values (`none`, `confirmed_volunteer`) carry no real
  // intranet role; only the schema-backed values map to an actual role list.
  const effectiveIntranetRoles =
    previewValue === INTRANET_PREVIEW_NONE ||
    previewValue === INTRANET_PREVIEW_CONFIRMED_VOLUNTEER
      ? []
      : ([previewValue] as IntranetRole[])

  return {
    effectiveRoles,
    effectiveIntranetRoles,
  }
}
